import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.my';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 md:px-6 text-center">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Статья не найдена
          </h1>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            На главную
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.image,
    url: articleUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: { '@type': 'Organization', name: 'YOYBUY.my', url: siteUrl },
    publisher: { '@type': 'Organization', name: 'YOYBUY.my', url: siteUrl, logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg' } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24 px-4 md:px-6">
        <div className="max-w-[800px] mx-auto">
          <nav aria-label="Хлебные крошки" className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6 flex-wrap">
            <a href="/" className="hover:text-accent-600 transition-colors cursor-pointer">Главная</a>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <a href="/#blog" className="hover:text-accent-600 transition-colors cursor-pointer">Блог</a>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>{new Date(article.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} чтения</span>
            </div>
          </div>

          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom">
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
              <strong className="text-foreground-950">{article.intro}</strong>
            </p>

            {article.sections.map((section, sIdx) => (
              <section key={sIdx} className="mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <div className="my-10 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200/60">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-2">
                Готовы начать покупки?
              </h3>
              <p className="text-sm md:text-base text-foreground-600 mb-4">
                Переходите на YOYBUY Sheet и организуйте закупки из Китая с максимальной эффективностью.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  YOYBUY Sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-bag-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              Полезные ссылки
            </h3>
            <div className="flex flex-wrap gap-3">
              <a href="/" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer underline">
                ← На главную YOYBUY.my
              </a>
              <span className="text-foreground-300">•</span>
              <a href="/#categories" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer underline">
                Категории товаров
              </a>
              <span className="text-foreground-300">•</span>
              <a href="/#about" className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer underline">
                О системе YOYBUY
              </a>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
        </div>
      </article>

      <Footer />
    </main>
  );
}